import { FC, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { X as XIcon } from '../../icons/x';
import {
    VIDEO_TUTORIALS,
    getVideoById,
    isPlaceholderVideo,
    markIntroVideoSeen
} from '../../lib/video-tutorials';

declare global {
    interface Window {
        YT?: any;
        onYouTubeIframeAPIReady?: () => void;
    }
}

let ytApiPromise: Promise<any> | null = null;

const loadYouTubeApi = (): Promise<any> => {
    if (ytApiPromise) return ytApiPromise;
    ytApiPromise = new Promise((resolve) => {
        if (window.YT && window.YT.Player) {
            resolve(window.YT);
            return;
        }
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
        const previousCallback = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof previousCallback === 'function') previousCallback();
            resolve(window.YT);
        };
    });
    return ytApiPromise;
};

interface VideoTutorialDialogProps {
    open: boolean;
    onClose: () => void;
    initialVideoId?: number;
    /**
     * When true, this is the post-signup intro flow:
     * watching video 0 to the end marks it as seen,
     * and a "Don't show again" button is shown.
     */
    isIntroFlow?: boolean;
}

export const VideoTutorialDialog: FC<VideoTutorialDialogProps> = ({
    open,
    onClose,
    initialVideoId = 0,
    isIntroFlow = false
}) => {
    const { t } = useTranslation();
    const [currentVideoId, setCurrentVideoId] = useState(initialVideoId);
    const [videoEnded, setVideoEnded] = useState(false);
    const playerContainerRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<any>(null);
    const playerElemIdRef = useRef<string>(`ipop-yt-${Math.random().toString(36).slice(2)}`);

    const currentVideo = getVideoById(currentVideoId);
    const showFollowUpMenu = videoEnded && currentVideoId === 0;
    const followUps = VIDEO_TUTORIALS.filter((v) => v.id !== 0);

    useEffect(() => {
        if (open) {
            setCurrentVideoId(initialVideoId);
            setVideoEnded(false);
        }
    }, [open, initialVideoId]);

    useEffect(() => {
        if (!open || !currentVideo || isPlaceholderVideo(currentVideo) || showFollowUpMenu) {
            return;
        }
        let canceled = false;
        loadYouTubeApi().then((YT) => {
            if (canceled || !playerContainerRef.current) return;
            if (playerRef.current) {
                try { playerRef.current.destroy(); } catch { /* ignore */ }
                playerRef.current = null;
            }
            playerContainerRef.current.innerHTML = `<div id="${playerElemIdRef.current}"></div>`;
            playerRef.current = new YT.Player(playerElemIdRef.current, {
                videoId: currentVideo.youtubeId,
                playerVars: {
                    autoplay: 1,
                    rel: 0,
                    modestbranding: 1
                },
                host: 'https://www.youtube-nocookie.com',
                events: {
                    onStateChange: (event: { data: number }) => {
                        if (event.data === YT.PlayerState.ENDED) {
                            setVideoEnded(true);
                            if (isIntroFlow && currentVideoId === 0) {
                                markIntroVideoSeen();
                            }
                        }
                    }
                }
            });
        });
        return () => {
            canceled = true;
            if (playerRef.current) {
                try { playerRef.current.destroy(); } catch { /* ignore */ }
                playerRef.current = null;
            }
        };
    }, [open, currentVideo, currentVideoId, showFollowUpMenu, isIntroFlow]);

    const handlePickFollowUp = (id: number) => {
        const next = getVideoById(id);
        if (!next || isPlaceholderVideo(next)) return;
        setCurrentVideoId(id);
        setVideoEnded(false);
    };

    const handleDontShowAgain = () => {
        markIntroVideoSeen();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box component="span">{currentVideo ? currentVideo.title : t('video_tutorial')}</Box>
                <IconButton onClick={onClose} size="small" aria-label="close">
                    <XIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: 'background.default' }}>
                {currentVideo && isPlaceholderVideo(currentVideo) ? (
                    <Alert severity="info" sx={{ my: 2 }}>
                        {t('video_not_uploaded_yet')}
                    </Alert>
                ) : showFollowUpMenu ? (
                    <Box sx={{ my: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('video_followup_title')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            {t('video_followup_subtitle')}
                        </Typography>
                        <List>
                            {followUps.map((v) => (
                                <ListItemButton
                                    key={v.id}
                                    onClick={() => handlePickFollowUp(v.id)}
                                    disabled={isPlaceholderVideo(v)}
                                >
                                    <ListItemIcon sx={{ minWidth: 40, fontWeight: 'bold', color: 'primary.main' }}>
                                        {v.id}.
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={v.title}
                                        secondary={isPlaceholderVideo(v) ? t('video_not_uploaded_yet') : undefined}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Box>
                ) : (
                    <Box
                        ref={playerContainerRef}
                        sx={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '56.25%',
                            my: 2,
                            '& > div': {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%'
                            },
                            '& iframe': {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 0
                            }
                        }}
                    />
                )}
            </DialogContent>
            <DialogActions>
                {isIntroFlow && !videoEnded && (
                    <Button color="inherit" onClick={handleDontShowAgain}>
                        {t('dont_show_again')}
                    </Button>
                )}
                <Button color="primary" variant="contained" onClick={onClose}>
                    {t('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
